// @givmo/passkeys — the words of the consoles' organization door (AS-WEB30): signing in through the
// employer's own identity provider, as the approved design page drew it (D-AS-48, version 5's
// section W and question 7).
//
// The door's LABEL is ruled per console:
//   - "Continue with Organization SSO" — APPROVED (D-AS-45's addendum, Jonathan 2026-10-07 10:48 PDT,
//     "Organization SSO"): the charity console's doors, Board's director door included, whose other
//     ways in say "Continue with …";
//   - "Sign in with Organization SSO" — APPROVED (D-AS-48 (7a)): the employer portal and the company
//     console, whose other ways in say "Sign in with …". The door takes each page's own verb, so only
//     the noun was the question.
// The lock notice's door button says what its page's door says.
//
// Every other line is APPROVED (D-AS-57, the company SSO copy sweep, 2026-10-16; D-AS-48 (4) had
// approved them as written), from the apps' company sign-in screen where one exists, with two
// approved step words (D-AS-35) read from the web reset's own table. "Company" stays a company word
// in the step while only a company can require its sign-in (company SSO ships first, D-AS-41); the
// label is the organization word already.
// Signed out, nothing names the company: the routing answer is uniform.

import { ESCAPE_HATCH_ENTRY, ESCAPE_HATCH_WORDS } from './escape-hatch-copy';
import { FACTOR_RESET_STEP_WORDS } from './factor-reset-copy';

/** Which verb the page's other ways in use: the door takes the same one. */
export type OrganizationSsoVerb = 'continue' | 'signIn';

/** The door's label on a page whose other ways in say "Continue with …" or "Sign in with …". */
export function organizationSsoLabel(verb: OrganizationSsoVerb): string {
  return verb === 'continue' ? 'Continue with Organization SSO' : 'Sign in with Organization SSO';
}

export const ORGANIZATION_SSO_WORDS = {
  /** APPROVED (D-AS-57): both apps' company sign-in screen (iOS workEmailTitle, Android auth_sso_email_title). */
  title: 'Sign in with your company',
  /** APPROVED (D-AS-57): both apps (iOS workEmailPrompt, Android auth_sso_email_subtitle). */
  prompt: 'Enter your work email and we’ll take you to your company’s sign-in page.',
  /** APPROVED (D-AS-57): both apps; the company console's own door already labels its field so. */
  emailLabel: 'Work email',
  /** APPROVED (D-AS-35): the web reset's step word, both apps' button. */
  continue: FACTOR_RESET_STEP_WORDS.verify,
  /** APPROVED (D-AS-57): while the company's page is open in its own window. */
  waiting:
    'Your company’s sign-in page is open in a new window. Finish signing in there, and you’ll come straight back.',
  /** APPROVED (D-AS-35). */
  cancel: FACTOR_RESET_STEP_WORDS.cancel,
  /** APPROVED (D-AS-57): on the way back, while the session is made. */
  signingIn: 'Signing you in…',
  /** APPROVED (D-AS-57): the step's way out, back to the door (the escape flow's words). */
  backToSignIn: ESCAPE_HATCH_WORDS.backToSignIn,
  /** EXISTING: Board's own words on its director forms' way back (`sign-in-screen.tsx`). */
  backToAllOptions: 'Back to all options',
  /** The escape hatch's entry, at the step's foot where a lock exists here (both apps do). */
  lostAccess: ESCAPE_HATCH_ENTRY,

  // ── The failures (APPROVED, D-AS-57), each the backend's own answer worded for the web ──
  /** `sso_available: false`: no company routes that domain (or not at a sign-in tier). */
  noSso:
    'That address doesn’t use company sign-in. Check that it’s your work email, or go back and sign in another way.',
  /** SSO_CEREMONY_FAILED from the callback or the exchange, or the window closed with no answer. */
  didNotFinish:
    'Company sign-in didn’t finish. Try again, or if your company’s sign-in page showed an error, ask your IT team.',
  /**
   * SSO_IDENTITY_NOT_LINKED while the "Lost access" link is shown: an explanation with two exits, so
   * the notice look, not red text.
   */
  notLinked: `Your work account isn’t connected to Givmo yet, so company sign-in can’t finish. If you can sign in another way, connect it in Settings in the Givmo app. If you can’t, use “${ESCAPE_HATCH_ENTRY}” below.`,
  /**
   * SSO_IDENTITY_NOT_LINKED while the link is hidden (no sign-in lock here): the one exit there is.
   * Pointing at a link the page doesn't show was D-AS-57's fix 4.
   */
  notLinkedNoEscape:
    'Your work account isn’t connected to Givmo yet, so company sign-in can’t finish. Sign in another way, then connect your work account in Settings in the Givmo app.',
  /** SSO_REDIRECT_URI_NOT_ALLOWED or SSO_NOT_CONFIGURED (this console isn't registered), or dark. */
  notHere: 'Company sign-in isn’t available right now. Please try again later.',
  /** A 429 (the escape flow's words). */
  throttled: ESCAPE_HATCH_WORDS.throttled,
} as const;
